<template>
    <section class="complex-regulation-requirement-item">
        <div class="complex-regulation-requirement-item__number">
            {{ number }}.
        </div>
        <div class="complex-regulation-requirement-item__content">
            <div
                class="complex-regulation-requirement-item__header complex-regulation-requirement-item__header--clickable"
                role="button"
                tabindex="0"
                :aria-controls="collapseId"
                :aria-expanded="expanded ? 'true' : 'false'"
                @click="toggleRequirement"
                @keydown.enter.self.prevent="toggleRequirement"
                @keydown.space.self.prevent="toggleRequirement"
            >
                <div>
                    <div class="d-flex align-items-center flex-wrap">
                        <h5 class="mb-0 mr-1">
                            {{ requirement.list.title }}
                        </h5>
                        <span
                            :id="statusExplanationId"
                            :class="hasBlockingReasons
                                ? 'complex-regulation-requirement-item__status-with-explanation'
                                : null"
                            :role="hasBlockingReasons ? 'button' : null"
                            :tabindex="hasBlockingReasons ? 0 : null"
                            :aria-label="hasBlockingReasons ? statusExplanationLabel : null"
                            @click="stopStatusToggle"
                        >
                            <b-badge :variant="statusVariant">{{ statusLabel }}</b-badge>
                            <feather-icon
                                v-if="hasBlockingReasons"
                                icon="InfoIcon"
                                size="14"
                            />
                        </span>
                        <span class="text-less-muted ml-1">{{ completionSummary }}</span>
                        <b-popover
                            :target="statusExplanationId"
                            :disabled="!hasBlockingReasons"
                            triggers="hover click blur"
                            placement="bottom"
                        >
                            <template #title>
                                {{ statusExplanationTitle }}
                            </template>
                            <ul class="complex-regulation-requirement-item__reasons mb-0 pl-1">
                                <li
                                    v-for="reason in blockingReasons"
                                    :key="reason.id"
                                >
                                    {{ reasonMessage(reason) }}
                                </li>
                            </ul>
                        </b-popover>
                    </div>
                    <div
                        v-if="legislationLabels.length"
                        class="complex-regulation-requirement-item__legislations"
                    >
                        {{ legislationLabels.join(', ') }}
                    </div>
                </div>
                <b-button
                    class="btn-icon"
                    size="sm"
                    variant="flat-secondary"
                    :aria-expanded="expanded ? 'true' : 'false'"
                    :aria-controls="collapseId"
                    :aria-label="toggleLabel"
                    @click.stop="toggleRequirement"
                >
                    <feather-icon :icon="expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'" />
                </b-button>
            </div>

            <b-collapse
                :id="collapseId"
                :visible="expanded"
            >
                <div class="complex-regulation-requirement-item__components">
                    <ComplexComponentRequirementItem
                        v-for="entry in requirement.componentEntries"
                        :key="entry.key"
                        :entry="entry"
                        :requirement="requirement"
                        :property-config="propertyConfig"
                        :expanded="isComponentExpanded(entry)"
                        :show-substance-requirement-status="showSubstanceRequirementStatus"
                        @toggle="toggleComponent(entry)"
                        @select="$emit('select', { ...$event, requirement })"
                        @action="$emit('action', $event)"
                    />
                </div>

            </b-collapse>
        </div>
    </section>
</template>

<script>
import {
    BBadge,
    BButton,
    BCollapse,
    BPopover,
} from 'bootstrap-vue'
import { blockingReasons } from '../requirement-checks'
import ComplexComponentRequirementItem from './ComplexComponentRequirementItem.vue'

export default {
    name: 'ComplexRegulationRequirementItem',
    components: {
        BBadge,
        BButton,
        BCollapse,
        BPopover,
        ComplexComponentRequirementItem,
    },
    props: {
        number: { type: Number, required: true },
        requirement: { type: Object, required: true },
        propertyConfig: { type: Object, required: true },
        expanded: { type: Boolean, default: true },
        showSubstanceRequirementStatus: { type: Boolean, default: true },
    },
    data() {
        return { expandedComponentKeys: {}, initialized: false }
    },
    computed: {
        collapseId() {
            return `complex-compliance-requirement-${this.requirement.id}`
        },
        completedComponents() {
            return this.requirement.componentEntries
                .filter(entry => entry.status === 'complete').length
        },
        completionSummary() {
            return `${this.completedComponents} / ${this.requirement.componentEntries.length}`
        },
        blockingReasons() {
            return blockingReasons(this.requirement.checks)
        },
        blockingReasonSignature() {
            return this.blockingReasons.map(reason => reason.id).join('|')
        },
        hasBlockingReasons() {
            return this.blockingReasons.length > 0
        },
        statusExplanationId() {
            return `complex-compliance-requirement-status-${this.requirement.id}`
        },
        statusExplanationTitle() {
            return this.translate(
                'Page(ReceivedRequest):Component(ComplianceInformation):status-explanation-title',
                'Why is information required?',
            )
        },
        statusExplanationLabel() {
            return `${this.statusLabel}. ${this.statusExplanationTitle}`
        },
        statusVariant() {
            return this.requirement.status === 'complete' ? 'light-success' : 'light-warning'
        },
        statusLabel() {
            return this.requirement.status === 'complete'
                ? this.translate('Component(Status):complete', 'Complete')
                : this.translate('Component(Status):information-required', 'Information required')
        },
        toggleLabel() {
            return this.expanded
                ? this.translate('Component(Button):collapse', 'Collapse')
                : this.translate('Component(Button):expand', 'Expand')
        },
        legislationLabels() {
            return this.requirement.legislations
                .map(legislation => legislation.title || legislation.name || legislation.key)
                .filter(Boolean)
        },
    },
    watch: {
        'requirement.componentEntries': {
            immediate: true,
            handler(entries) {
                if (this.initialized || !entries.length) {
                    return
                }
                entries.forEach(entry => this.$set(this.expandedComponentKeys, entry.key, false))
                const firstIncomplete = entries.find(entry => entry.status !== 'complete')
                if (firstIncomplete) {
                    this.$set(this.expandedComponentKeys, firstIncomplete.key, true)
                }
                this.initialized = true
            },
        },
        blockingReasonSignature() {
            this.hideStatusExplanation()
        },
    },
    beforeDestroy() {
        this.hideStatusExplanation()
    },
    methods: {
        toggleRequirement() {
            this.$emit('toggle')
        },
        stopStatusToggle(event) {
            if (this.hasBlockingReasons) {
                event.stopPropagation()
            }
        },
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
        reasonMessage(reason) {
            return this.translate(reason.messageKey, reason.fallbackMessage)
        },
        hideStatusExplanation() {
            this.$root.$emit('bv::hide::popover', this.statusExplanationId)
        },
        isComponentExpanded(entry) {
            return this.expandedComponentKeys[entry.key] === true
        },
        toggleComponent(entry) {
            this.$set(this.expandedComponentKeys, entry.key, !this.isComponentExpanded(entry))
        },
    },
}
</script>

<style lang="scss" scoped>
.complex-regulation-requirement-item {
    display: grid;
    gap: 1rem;
    grid-template-columns: 2rem minmax(0, 1fr);
    padding: 1.5rem 0;

    & + & {
        border-top: 1px solid #ebe9f1;
    }

    &__number {
        color: #5e5873;
        font-size: 1.1rem;
        font-weight: 600;
        padding-top: .15rem;
    }

    &__header {
        align-items: flex-start;
        display: flex;
        gap: 1rem;
        justify-content: space-between;
    }

    &__header--clickable {
        cursor: pointer;
        outline: none;

        &:focus-visible {
            border-radius: .357rem;
            box-shadow: 0 0 0 .2rem rgba(115, 103, 240, .25);
        }
    }

    &__legislations {
        color: #6e6b7b;
        font-size: .875rem;
        margin-top: .35rem;
    }

    &__status-with-explanation {
        align-items: center;
        color: #6e6b7b;
        cursor: help;
        display: inline-flex;
        gap: .35rem;
        outline: none;
    }

    &__reasons {
        min-width: 15rem;

        li + li {
            margin-top: .35rem;
        }
    }

    &__components {
        margin-top: 1rem;
    }

}

@media (max-width: 767.98px) {
    .complex-regulation-requirement-item {
        grid-template-columns: 1fr;

        &__number {
            display: none;
        }
    }
}
</style>
